import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RIDE_REPORT_LABELS, type Order, type RideReport } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { useSession } from '../../session';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const amount = (r: RideReport) => (r.quantity === null ? '' : `${r.quantity.toLocaleString('de-DE')} ${r.unit === 'km' ? 'km' : 'Minuten'}`);

/**
 * Nachmeldungen des Fahrers (KON-264): Nach dem Abschluss meldet der Fahrer in der App zusätzliche Kilometer, zusätzliche Wartezeit oder eine Notiz nach. Hier sieht das Büro sie, rechnet
 * nach (zum Beispiel mit einer zusätzlichen Rechnungsposition) oder nimmt die Notiz zur Kenntnis und markiert die Meldung als erledigt. Der Bereich erscheint erst, wenn es Meldungen gibt.
 */
export function RideReportsPanel({ order }: { order: Order }) {
  const { can, session } = useSession();
  const allowed = session?.tenant.industry === 'chauffeur' && can('orders.read');
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const reports = useQuery({ queryKey: ['ride-reports', order.id], queryFn: async () => (await api<{ data: RideReport[] }>('GET', `/orders/${order.id}/ride-reports/office`)).data, enabled: allowed });
  const handle = useMutation({
    mutationFn: async (v: { id: string; handled: boolean }) => api('POST', `/orders/${order.id}/ride-reports/${v.id}/handled`, { handled: v.handled }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['ride-reports', order.id] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Das hat nicht geklappt.'),
  });

  const list = reports.data ?? [];
  if (!allowed || list.length === 0) return null;
  const open = list.filter((r) => !r.handledAt).length;
  const write = can('orders.write');

  return (
    <CollapsibleCard title={`Nachmeldungen des Fahrers${open > 0 ? ` (${open} offen)` : ''}`} storageKey="order-ride-reports" defaultOpen>
      {open > 0 && <div className="alert alert--warn" role="status">Der Fahrer hat nach dem Abschluss Angaben nachgemeldet. Bitte rechnen Sie nach oder nehmen Sie sie zur Kenntnis und markieren Sie sie als erledigt.</div>}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr><th>Art</th><th>Menge</th><th>Notiz</th><th>Fahrer</th><th>Gemeldet</th><th>Stand</th><th /></tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id}>
                <td data-label="Art"><strong>{RIDE_REPORT_LABELS[r.kind]}</strong></td>
                <td data-label="Menge">{amount(r) || '–'}</td>
                <td data-label="Notiz">{r.note ?? '–'}</td>
                <td data-label="Fahrer">{r.driverName ?? '–'}</td>
                <td data-label="Gemeldet">{stamp.format(new Date(r.createdAt))} Uhr</td>
                <td data-label="Stand"><span className={`badge ${r.handledAt ? 'badge--won' : 'badge--in_progress'}`}>{r.handledAt ? 'Erledigt' : 'Offen'}</span></td>
                <td>
                  {write && (
                    <button type="button" className="btn btn--secondary btn--sm" disabled={handle.isPending} onClick={() => handle.mutate({ id: r.id, handled: !r.handledAt })}>
                      {r.handledAt ? 'Wieder öffnen' : 'Erledigt'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleCard>
  );
}
